import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAdmin } from '../../context/AdminContext';
import '../../styles/Admin.css';
import { toSitePath } from 'utils/sitePath';

const MEDUSA_BACKEND_URL = process.env.REACT_APP_MEDUSA_BACKEND_URL || 'http://localhost:9000';

// Acordeones por defecto
const DEFAULT_DETAILS = [
  { title: 'Sintetizador de colágeno', content: '' },
  { title: 'Poderoso antioxidante', content: '' },
  { title: 'Hiperpigmentación', content: '' },
  { title: 'Diferencia entre concentraciones', content: '' },
  { title: 'Combinaciones con activos', content: '' },
  { title: 'Contraindicaciones', content: '' },
  { title: 'Modo de uso detallado', content: '' }
];

export default function AdminProductEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token, isAuthenticated } = useAdmin();

  const [product, setProduct] = useState(null);
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingReviews, setUploadingReviews] = useState(false);
  const reviewsFileRef = useRef(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const messageTimerRef = useRef(null);

  useEffect(() => {
    return () => { if (messageTimerRef.current) clearTimeout(messageTimerRef.current); };
  }, []);
  const [activeTab, setActiveTab] = useState('general');
  const [showPreview, setShowPreview] = useState(false);

  // Metadata fields
  const [metadata, setMetadata] = useState({
    // Grid/Tienda
    subtitle: '',
    badge: '',
    short_description: '',
    reviews_image: '', // Foto de la sección de reseñas (mitad derecha), por producto

    // Beneficios
    benefits_label: '',
    benefits_description: '',
    benefits_items: [], // Lista libre (kits): [{ title, content }]
    texture_image: '',
    texture_video: '',
    media_type: 'image', // 'image' o 'video'
    
    // Ingredientes
    active_ingredients: '',
    ingredients_detail: '',
    
    // Uso
    how_to_use: '',
    dosis: '',
    aroma: '',
    
    // Detalles (acordeones)
    details_label: '',      // Etiqueta de la sección (default "Detalles")
    details_sublabel: '',   // Subetiqueta opcional (ej. "Resultados visibles")
    more_details: DEFAULT_DETAILS,
    details_image: '',
    details_video: '',
    details_media_type: 'image',
    
    // Relaciones
    sinergia_ids: '',
    recommended_ids: ''
  });

  const fetchProduct = useCallback(async () => {
    try {
      const response = await fetch(
        `${MEDUSA_BACKEND_URL}/admin/products/${id}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setProduct(data.product);
        
        const existingMetadata = data.product.metadata || {};
        setMetadata({
          subtitle: existingMetadata.subtitle || '',
          badge: existingMetadata.badge || '',
          reviews_image: existingMetadata.reviews_image || '',
          benefits_label: existingMetadata.benefits_label || '',
          benefits_description: existingMetadata.benefits_description || '',
          benefits_items: Array.isArray(existingMetadata.benefits_items) ? existingMetadata.benefits_items : [],
          texture_image: existingMetadata.texture_image || '',
          texture_video: existingMetadata.texture_video || '',
          media_type: existingMetadata.texture_video ? 'video' : 'image',
          active_ingredients: existingMetadata.active_ingredients || '',
          ingredients_detail: existingMetadata.ingredients_detail || '',
          how_to_use: existingMetadata.how_to_use || '',
          dosis: existingMetadata.dosis || '',
          aroma: existingMetadata.aroma || '',
          details_label: existingMetadata.details_label || '',
          details_sublabel: existingMetadata.details_sublabel || '',
          more_details: existingMetadata.more_details || DEFAULT_DETAILS,
          details_image: existingMetadata.details_image || '',
          details_video: existingMetadata.details_video || '',
          details_media_type: existingMetadata.details_video ? 'video' : 'image',
          sinergia_ids: Array.isArray(existingMetadata.sinergia_ids) 
            ? existingMetadata.sinergia_ids.join(', ') 
            : existingMetadata.sinergia_ids || '',
          recommended_ids: Array.isArray(existingMetadata.recommended_ids) 
            ? existingMetadata.recommended_ids.join(', ') 
            : existingMetadata.recommended_ids || ''
        });
      }
    } catch (error) {
      console.error('Error fetching product:', error);
      setMessage({ type: 'error', text: 'Error al cargar el producto' });
    } finally {
      setLoading(false);
    }
  }, [id, token]);

  const fetchAllProducts = useCallback(async () => {
    try {
      const response = await fetch(
        `${MEDUSA_BACKEND_URL}/admin/products?limit=100`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setAllProducts(data.products || []);
      }
    } catch (error) {
      console.error('Error fetching all products:', error);
    }
  }, [token]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin');
      return;
    }
    fetchProduct();
    fetchAllProducts();
  }, [id, isAuthenticated, navigate, fetchProduct, fetchAllProducts]);

  const handleChange = (field, value) => {
    setMetadata(prev => ({ ...prev, [field]: value }));
  };

  // Sube una imagen a R2 (vía el file provider de Medusa) y guarda la URL en
  // reviews_image. Reutiliza el token de admin como el resto de llamadas.
  const handleReviewsUpload = async (file) => {
    if (!file) return;
    setUploadingReviews(true);
    setMessage({ type: '', text: '' });
    try {
      const form = new FormData();
      form.append('files', file);
      const res = await fetch(`${MEDUSA_BACKEND_URL}/admin/uploads`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: form,
      });
      if (!res.ok) throw new Error('upload');
      const data = await res.json();
      const url = data?.files?.[0]?.url;
      if (!url) throw new Error('sin url');
      handleChange('reviews_image', url);
      setMessage({ type: 'success', text: 'Imagen subida. Recuerda "Guardar" para aplicarla.' });
    } catch (error) {
      console.error('Error subiendo imagen de reseñas:', error);
      setMessage({ type: 'error', text: 'No se pudo subir la imagen' });
    } finally {
      setUploadingReviews(false);
    }
  };

  const handleDetailChange = (index, field, value) => {
    const newDetails = [...metadata.more_details];
    newDetails[index] = { ...newDetails[index], [field]: value };
    setMetadata(prev => ({ ...prev, more_details: newDetails }));
  };

  const addDetail = () => {
    setMetadata(prev => ({
      ...prev,
      more_details: [...prev.more_details, { title: '', content: '' }]
    }));
  };

  const removeDetail = (index) => {
    setMetadata(prev => ({
      ...prev,
      more_details: prev.more_details.filter((_, i) => i !== index)
    }));
  };

  // Handlers para la lista libre de beneficios (kits/sets)
  const handleBenefitItemChange = (index, field, value) => {
    const newItems = [...metadata.benefits_items];
    newItems[index] = { ...newItems[index], [field]: value };
    setMetadata(prev => ({ ...prev, benefits_items: newItems }));
  };

  const addBenefitItem = () => {
    setMetadata(prev => ({
      ...prev,
      benefits_items: [...prev.benefits_items, { title: '', content: '' }]
    }));
  };

  const removeBenefitItem = (index) => {
    setMetadata(prev => ({
      ...prev,
      benefits_items: prev.benefits_items.filter((_, i) => i !== index)
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      const sinergiaIds = metadata.sinergia_ids
        .split(',')
        .map(id => id.trim())
        .filter(id => id !== '');
      
      const recommendedIds = metadata.recommended_ids
        .split(',')
        .map(id => id.trim())
        .filter(id => id !== '');

      const metadataToSave = {
        subtitle: metadata.subtitle,
        badge: metadata.badge,
        reviews_image: toSitePath(metadata.reviews_image),
        benefits_label: metadata.benefits_label,
        benefits_description: metadata.benefits_description,
        benefits_items: metadata.benefits_items.filter(i => i.title.trim() !== ''),
        texture_image: metadata.media_type === 'image' ? toSitePath(metadata.texture_image) : '',
        texture_video: metadata.media_type === 'video' ? toSitePath(metadata.texture_video) : '',
        active_ingredients: metadata.active_ingredients,
        ingredients_detail: metadata.ingredients_detail,
        how_to_use: metadata.how_to_use,
        dosis: metadata.dosis,
        aroma: metadata.aroma,
        details_label: metadata.details_label,
        details_sublabel: metadata.details_sublabel,
        more_details: metadata.more_details.filter(d => d.title.trim() !== ''),
        details_image: metadata.details_media_type === 'image' ? toSitePath(metadata.details_image) : '',
        details_video: metadata.details_media_type === 'video' ? toSitePath(metadata.details_video) : '',
        sinergia_ids: sinergiaIds,
        recommended_ids: recommendedIds
      };

      const response = await fetch(
        `${MEDUSA_BACKEND_URL}/admin/products/${id}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ metadata: metadataToSave }),
        }
      );

      if (response.ok) {
        setMessage({ type: 'success', text: '¡Producto actualizado correctamente!' });
        messageTimerRef.current = setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      } else {
        throw new Error('Error al guardar');
      }
    } catch (error) {
      console.error('Error saving product:', error);
      setMessage({ type: 'error', text: 'Error al guardar el producto' });
    } finally {
      setSaving(false);
    }
  };

  const toggleProductSelection = (productId, field) => {
    const currentIds = metadata[field]
      .split(',')
      .map(id => id.trim())
      .filter(id => id !== '');
    
    const index = currentIds.indexOf(productId);
    
    if (index > -1) {
      currentIds.splice(index, 1);
    } else {
      currentIds.push(productId);
    }
    
    setMetadata(prev => ({ ...prev, [field]: currentIds.join(', ') }));
  };

  const isProductSelected = (productId, field) => {
    return metadata[field].includes(productId);
  };

  const getCompletionStatus = () => {
    const requiredFields = [
      metadata.subtitle,
      metadata.benefits_label,
      metadata.benefits_description,
      metadata.active_ingredients,
      metadata.how_to_use
    ];
    const completed = requiredFields.filter(f => f && f.trim() !== '').length;
    return { completed, total: requiredFields.length };
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="admin-loading">Cargando producto...</div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="admin-page">
        <div className="admin-error">Producto no encontrado</div>
      </div>
    );
  }

  const { completed, total } = getCompletionStatus();
  const completionPercent = Math.round((completed / total) * 100);

  const tabs = [
    { id: 'general', label: 'General', icon: '📝' },
    { id: 'benefits', label: 'Beneficios', icon: '✨' },
    { id: 'details', label: 'Detalles', icon: '📋' },
    { id: 'relations', label: 'Relaciones', icon: '🔗' }
  ];

  return (
    <div className="admin-page admin-edit-page">
      {/* Header */}
      <header className="admin-header">
        <div className="admin-header-left">
          <Link to="/admin/products" className="admin-back-link">
            ← Volver
          </Link>
          <div className="admin-header-title">
            <h1>{product.title}</h1>
            <div className="admin-completion-badge">
              <div className="admin-completion-bar">
                <div 
                  className="admin-completion-fill"
                  style={{ width: `${completionPercent}%` }}
                />
              </div>
              <span>{completionPercent}% completo</span>
            </div>
          </div>
        </div>
        <div className="admin-header-right">
          <button 
            onClick={() => setShowPreview(!showPreview)}
            className="admin-preview-btn"
          >
            {showPreview ? '✕ Cerrar preview' : '👁 Preview'}
          </button>
          <button 
            onClick={handleSave} 
            className="admin-save-btn"
            disabled={saving}
          >
            {saving ? 'Guardando...' : '💾 Guardar'}
          </button>
        </div>
      </header>

      {/* Message */}
      {message.text && (
        <div className={`admin-message ${message.type}`}>
          {message.type === 'success' ? '✓ ' : '⚠ '}{message.text}
        </div>
      )}

      {/* Main Content */}
      <div className="admin-edit-layout">
        {/* Sidebar con imagen */}
        <aside className="admin-edit-sidebar">
          <div className="admin-product-preview-card">
            {product.thumbnail && (
              <img src={product.thumbnail} alt={product.title} />
            )}
            <div className="admin-preview-info">
              <h3>{product.title}</h3>
              <p className="admin-preview-subtitle">
                {metadata.subtitle || 'Sin subtítulo'}
              </p>
              {metadata.badge && (
                <span className={`admin-badge admin-badge-${metadata.badge}`}>
                  {metadata.badge === 'launch' && 'Lanzamiento'}
                  {metadata.badge === 'essential' && 'Esencial'}
                  {metadata.badge === 'basic' && 'Básico'}
                </span>
              )}
            </div>
          </div>

          {/* Quick Stats */}
          <div className="admin-quick-stats">
            <div className="admin-stat">
              <span className="admin-stat-label">Campos requeridos</span>
              <span className="admin-stat-value">{completed}/{total}</span>
            </div>
            <div className="admin-stat">
              <span className="admin-stat-label">Sinergia</span>
              <span className="admin-stat-value">
                {metadata.sinergia_ids.split(',').filter(id => id.trim()).length}
              </span>
            </div>
            <div className="admin-stat">
              <span className="admin-stat-label">Recomendados</span>
              <span className="admin-stat-value">
                {metadata.recommended_ids.split(',').filter(id => id.trim()).length}
              </span>
            </div>
          </div>
        </aside>

        {/* Main Form */}
        <main className="admin-edit-main">
          {/* Tabs */}
          <div className="admin-tabs">
            {tabs.map(tab => (
              <button
                key={tab.id}
                className={`admin-tab ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <span className="admin-tab-icon">{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="admin-tab-content">
            {/* GENERAL TAB */}
            {activeTab === 'general' && (
              <div className="admin-form-section">
                <div className="admin-section-header">
                  <h3>Información General</h3>
                  <p>Información que aparece en el grid de la tienda</p>
                </div>

                <div className="admin-field">
                  <label>
                    Subtítulo <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    value={metadata.subtitle}
                    onChange={(e) => handleChange('subtitle', e.target.value)}
                    placeholder="Ej: Ayudar a disminuir la pigmentación, refinar los poros"
                    className={metadata.subtitle ? 'filled' : ''}
                  />
                  <span className="admin-field-hint">
                    Se muestra debajo del nombre en el grid y single product
                  </span>
                </div>

                <div className="admin-field">
  <label>
    Descripción corta <span className="required">*</span>
  </label>
  <textarea
    value={metadata.short_description}
    onChange={(e) => handleChange('short_description', e.target.value)}
    placeholder="Ej: Serum con niacinamida para controlar el sebo y minimizar poros"
    rows={2}
    className={metadata.short_description ? 'filled' : ''}
  />
  <span className="admin-field-hint">
    Descripción que aparece en el hero del producto
  </span>
</div>

                <div className="admin-field">
                  <label>Badge/Etiqueta</label>
                  <div className="admin-badge-selector">
                    <button
                      className={`admin-badge-option ${metadata.badge === '' ? 'active' : ''}`}
                      onClick={() => handleChange('badge', '')}
                    >
                      Sin badge
                    </button>
                    <button
                      className={`admin-badge-option badge-launch ${metadata.badge === 'launch' ? 'active' : ''}`}
                      onClick={() => handleChange('badge', 'launch')}
                    >
                      🚀 Lanzamiento
                    </button>
                    <button
                      className={`admin-badge-option badge-essential ${metadata.badge === 'essential' ? 'active' : ''}`}
                      onClick={() => handleChange('badge', 'essential')}
                    >
                      ⭐ Esencial
                    </button>
                    <button
                      className={`admin-badge-option badge-basic ${metadata.badge === 'basic' ? 'active' : ''}`}
                      onClick={() => handleChange('badge', 'basic')}
                    >
                      ✓ Básico
                    </button>
                  </div>
                </div>

                <div className="admin-divider" />

                <div className="admin-section-header">
                  <h3>Ingredientes</h3>
                </div>

                <div className="admin-field">
                  <label>
                    Ingredientes activos <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    value={metadata.active_ingredients}
                    onChange={(e) => handleChange('active_ingredients', e.target.value)}
                    placeholder="Ej: Vitamina C, Ácido Ferúlico, Vitamina E"
                    className={metadata.active_ingredients ? 'filled' : ''}
                  />
                </div>

                <div className="admin-field">
                  <label>Lista completa (INCI)</label>
                  <textarea
                    value={metadata.ingredients_detail}
                    onChange={(e) => handleChange('ingredients_detail', e.target.value)}
                    placeholder="Lista completa de ingredientes según nomenclatura INCI..."
                    rows={4}
                  />
                </div>

                <div className="admin-divider" />

                <div className="admin-section-header">
                  <h3>Modo de Uso</h3>
                </div>

                <div className="admin-field">
                  <label>
                    Instrucciones <span className="required">*</span>
                  </label>
                  <textarea
                    value={metadata.how_to_use}
                    onChange={(e) => handleChange('how_to_use', e.target.value)}
                    placeholder="Ej: Aplicar sobre la piel limpia y seca, mañana y noche..."
                    rows={3}
                    className={metadata.how_to_use ? 'filled' : ''}
                  />
                </div>

                <div className="admin-field-row">
                  <div className="admin-field">
                    <label>Dosis</label>
                    <input
                      type="text"
                      value={metadata.dosis}
                      onChange={(e) => handleChange('dosis', e.target.value)}
                      placeholder="Ej: De 4 a 5 gotas"
                    />
                  </div>
                  <div className="admin-field">
                    <label>Aroma</label>
                    <input
                      type="text"
                      value={metadata.aroma}
                      onChange={(e) => handleChange('aroma', e.target.value)}
                      placeholder="Ej: Libre de fragancia"
                    />
                  </div>
                </div>

                <div className="admin-divider" />

                <div className="admin-section-header">
                  <h3>Imagen de reseñas</h3>
                  <p>Foto que aparece en la sección de reseñas de este producto (mitad derecha). Es independiente de la galería: cámbiala cuando quieras.</p>
                </div>

                <div className="admin-field">
                  <label>Imagen de reseñas</label>
                  <input
                    type="url"
                    value={metadata.reviews_image}
                    onChange={(e) => handleChange('reviews_image', e.target.value)}
                    placeholder='https://... (o usa "Subir imagen")'
                    className={metadata.reviews_image ? 'filled' : ''}
                  />
                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      className="admin-media-btn"
                      onClick={() => reviewsFileRef.current?.click()}
                      disabled={uploadingReviews}
                    >
                      {uploadingReviews ? 'Subiendo…' : '⬆ Subir imagen'}
                    </button>
                    {metadata.reviews_image && (
                      <button
                        type="button"
                        className="admin-media-btn"
                        onClick={() => handleChange('reviews_image', '')}
                      >
                        Quitar
                      </button>
                    )}
                  </div>
                  <input
                    ref={reviewsFileRef}
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) handleReviewsUpload(f); e.target.value = ''; }}
                  />
                  <span className="admin-field-hint">
                    Si la dejas vacía, se usará la última imagen de la galería del producto.
                  </span>
                  {metadata.reviews_image && (
                    <div className="admin-media-preview">
                      <img src={metadata.reviews_image} alt="Preview reseñas" />
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* BENEFITS TAB */}
            {activeTab === 'benefits' && (
              <div className="admin-form-section">
                <div className="admin-section-header">
                  <h3>Sección de Beneficios</h3>
                  <p>Aparece debajo del hero con imagen/video a la izquierda</p>
                </div>

                <div className="admin-field">
                  <label>
                    Título de beneficios <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    value={metadata.benefits_label}
                    onChange={(e) => handleChange('benefits_label', e.target.value)}
                    placeholder="Ej: Luminosidad a tu piel"
                    className={metadata.benefits_label ? 'filled' : ''}
                  />
                </div>

                <div className="admin-field">
                  <label>
                    Descripción <span className="required">*</span>
                  </label>
                  <textarea
                    value={metadata.benefits_description}
                    onChange={(e) => handleChange('benefits_description', e.target.value)}
                    placeholder="Descripción detallada de los beneficios del producto..."
                    rows={4}
                    className={metadata.benefits_description ? 'filled' : ''}
                  />
                </div>

                <div className="admin-divider" />

                <div className="admin-section-header">
                  <h3>Lista de beneficios (kits/sets)</h3>
                  <p>Cada ítem es un producto/beneficio: título (ej. "Niacinamida 4%, 30 mL") y descripción. Se muestra como lista debajo del encabezado. Déjalo vacío en productos individuales.</p>
                </div>

                <div className="admin-accordion-editor">
                  {metadata.benefits_items.map((item, index) => (
                    <div key={index} className="admin-accordion-item">
                      <div className="admin-accordion-header">
                        <span className="admin-accordion-number">{index + 1}</span>
                        <input
                          type="text"
                          value={item.title}
                          onChange={(e) => handleBenefitItemChange(index, 'title', e.target.value)}
                          placeholder="Ej: Niacinamida 4%, 30 mL"
                          className="admin-accordion-title-input"
                        />
                        <button
                          className="admin-accordion-delete"
                          onClick={() => removeBenefitItem(index)}
                          title="Eliminar"
                        >
                          ✕
                        </button>
                      </div>
                      <textarea
                        value={item.content}
                        onChange={(e) => handleBenefitItemChange(index, 'content', e.target.value)}
                        placeholder="Descripción (ej. Serum que unifica el tono, combate acné...)"
                        rows={2}
                        className="admin-accordion-content-input"
                      />
                    </div>
                  ))}

                  <button className="admin-add-accordion" onClick={addBenefitItem}>
                    + Agregar ítem
                  </button>
                </div>

                <div className="admin-divider" />

                <div className="admin-section-header">
                  <h3>Media de Beneficios</h3>
                  <p>Imagen o video que aparece junto a los beneficios</p>
                </div>

                <div className="admin-media-toggle">
                  <button
                    className={`admin-media-btn ${metadata.media_type === 'image' ? 'active' : ''}`}
                    onClick={() => handleChange('media_type', 'image')}
                  >
                    🖼 Imagen
                  </button>
                  <button
                    className={`admin-media-btn ${metadata.media_type === 'video' ? 'active' : ''}`}
                    onClick={() => handleChange('media_type', 'video')}
                  >
                    🎬 Video
                  </button>
                </div>

                {metadata.media_type === 'image' ? (
                  <div className="admin-field">
                    <label>URL de imagen de textura</label>
                    <input
                      type="url"
                      value={metadata.texture_image}
                      onChange={(e) => handleChange('texture_image', e.target.value)}
                      placeholder="https://..."
                    />
                    {metadata.texture_image && (
                      <div className="admin-media-preview">
                        <img src={metadata.texture_image} alt="Preview" />
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="admin-field">
                    <label>URL de video (YouTube, Vimeo, o MP4)</label>
                    <input
                      type="url"
                      value={metadata.texture_video}
                      onChange={(e) => handleChange('texture_video', e.target.value)}
                      placeholder="https://www.youtube.com/watch?v=... o https://vimeo.com/..."
                    />
                    <span className="admin-field-hint">
                      Soporta YouTube, Vimeo o enlaces directos a MP4
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* DETAILS TAB */}
            {activeTab === 'details' && (
              <div className="admin-form-section">
                <div className="admin-section-header">
                  <h3>Sección "Más Detalles"</h3>
                  <p>Acordeones expandibles con información adicional</p>
                </div>

                <div className="admin-field-row">
                  <div className="admin-field">
                    <label>Etiqueta de la sección</label>
                    <input
                      type="text"
                      value={metadata.details_label}
                      onChange={(e) => handleChange('details_label', e.target.value)}
                      placeholder='Default: "Detalles" (ej. "Beneficios combinados")'
                    />
                  </div>
                  <div className="admin-field">
                    <label>Subetiqueta (opcional)</label>
                    <input
                      type="text"
                      value={metadata.details_sublabel}
                      onChange={(e) => handleChange('details_sublabel', e.target.value)}
                      placeholder='Ej: "Resultados visibles"'
                    />
                  </div>
                </div>

                <div className="admin-divider" />

                <div className="admin-accordion-editor">
                  {metadata.more_details.map((detail, index) => (
                    <div key={index} className="admin-accordion-item">
                      <div className="admin-accordion-header">
                        <span className="admin-accordion-number">{index + 1}</span>
                        <input
                          type="text"
                          value={detail.title}
                          onChange={(e) => handleDetailChange(index, 'title', e.target.value)}
                          placeholder="Título del acordeón"
                          className="admin-accordion-title-input"
                        />
                        <button
                          className="admin-accordion-delete"
                          onClick={() => removeDetail(index)}
                          title="Eliminar"
                        >
                          ✕
                        </button>
                      </div>
                      <textarea
                        value={detail.content}
                        onChange={(e) => handleDetailChange(index, 'content', e.target.value)}
                        placeholder="Contenido que se mostrará al expandir..."
                        rows={3}
                        className="admin-accordion-content-input"
                      />
                    </div>
                  ))}
                  
                  <button className="admin-add-accordion" onClick={addDetail}>
                    + Agregar acordeón
                  </button>
                </div>

                <div className="admin-divider" />

                <div className="admin-section-header">
                  <h3>Media de Detalles</h3>
                  <p>Imagen o video que aparece a la derecha de los acordeones</p>
                </div>

                <div className="admin-media-toggle">
                  <button
                    className={`admin-media-btn ${metadata.details_media_type === 'image' ? 'active' : ''}`}
                    onClick={() => handleChange('details_media_type', 'image')}
                  >
                    🖼 Imagen
                  </button>
                  <button
                    className={`admin-media-btn ${metadata.details_media_type === 'video' ? 'active' : ''}`}
                    onClick={() => handleChange('details_media_type', 'video')}
                  >
                    🎬 Video
                  </button>
                </div>

                {metadata.details_media_type === 'image' ? (
                  <div className="admin-field">
                    <label>URL de imagen</label>
                    <input
                      type="url"
                      value={metadata.details_image}
                      onChange={(e) => handleChange('details_image', e.target.value)}
                      placeholder="https://..."
                    />
                    {metadata.details_image && (
                      <div className="admin-media-preview">
                        <img src={metadata.details_image} alt="Preview" />
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="admin-field">
                    <label>URL de video</label>
                    <input
                      type="url"
                      value={metadata.details_video}
                      onChange={(e) => handleChange('details_video', e.target.value)}
                      placeholder="https://www.youtube.com/watch?v=..."
                    />
                  </div>
                )}
              </div>
            )}

            {/* RELATIONS TAB */}
            {activeTab === 'relations' && (
              <div className="admin-form-section">
                <div className="admin-section-header">
                  <h3>Productos Sinergia</h3>
                  <p>Productos complementarios que aparecen en el hero (máx. 3)</p>
                </div>

                <div className="admin-products-grid-selector">
                  {allProducts
                    .filter(p => p.id !== id)
                    .map(p => (
                      <div 
                        key={p.id}
                        className={`admin-product-select-card ${isProductSelected(p.id, 'sinergia_ids') ? 'selected' : ''}`}
                        onClick={() => toggleProductSelection(p.id, 'sinergia_ids')}
                      >
                        <div className="admin-select-checkbox">
                          {isProductSelected(p.id, 'sinergia_ids') && '✓'}
                        </div>
                        {p.thumbnail && (
                          <img src={p.thumbnail} alt={p.title} />
                        )}
                        <span className="admin-select-title">{p.title}</span>
                      </div>
                    ))
                  }
                </div>

                <div className="admin-divider" />

                <div className="admin-section-header">
                  <h3>Productos Recomendados</h3>
                  <p>Aparecen en el carousel al final de la página</p>
                </div>

                <div className="admin-products-grid-selector">
                  {allProducts
                    .filter(p => p.id !== id)
                    .map(p => (
                      <div 
                        key={p.id}
                        className={`admin-product-select-card ${isProductSelected(p.id, 'recommended_ids') ? 'selected' : ''}`}
                        onClick={() => toggleProductSelection(p.id, 'recommended_ids')}
                      >
                        <div className="admin-select-checkbox">
                          {isProductSelected(p.id, 'recommended_ids') && '✓'}
                        </div>
                        {p.thumbnail && (
                          <img src={p.thumbnail} alt={p.title} />
                        )}
                        <span className="admin-select-title">{p.title}</span>
                      </div>
                    ))
                  }
                </div>
              </div>
            )}
          </div>

          {/* Bottom Save */}
          <div className="admin-form-footer">
            <button 
              onClick={handleSave} 
              className="admin-save-btn large"
              disabled={saving}
            >
              {saving ? 'Guardando...' : '💾 Guardar cambios'}
            </button>
          </div>
        </main>

        {/* Preview Panel */}
        {showPreview && (
          <aside className="admin-preview-panel">
            <div className="admin-preview-header">
              <h3>Vista Previa</h3>
              <button onClick={() => setShowPreview(false)}>✕</button>
            </div>
            <div className="admin-preview-content">
              <div className="preview-section">
                <h4>Grid de Tienda</h4>
                <div className="preview-card-mock">
                  <div className="preview-card-image">
                    {product.thumbnail && <img src={product.thumbnail} alt="" />}
                    {metadata.badge && (
                      <span className={`preview-badge ${metadata.badge}`}>
                        {metadata.badge === 'launch' && 'Lanzamiento'}
                        {metadata.badge === 'essential' && 'Esencial en tu rutina'}
                        {metadata.badge === 'basic' && 'Básico en tu rutina'}
                      </span>
                    )}
                  </div>
                  <div className="preview-card-info">
                    <h5>{product.title}</h5>
                    <p>{metadata.subtitle || 'Sin subtítulo'}</p>
                  </div>
                </div>
              </div>

              <div className="preview-section">
                <h4>Beneficios</h4>
                <div className="preview-benefits">
                  <span className="preview-label">{metadata.benefits_label || 'Título de beneficios'}</span>
                  <p>{metadata.benefits_description || 'Descripción de beneficios...'}</p>
                </div>
              </div>

              <div className="preview-section">
                <h4>Detalles ({metadata.more_details.filter(d => d.title).length} acordeones)</h4>
                <div className="preview-accordions">
                  {metadata.more_details.filter(d => d.title).map((d, i) => (
                    <div key={i} className="preview-accordion">
                      {d.title}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </aside>
        )}
      </div>
    </div>
  );
}
